import { createClient, type SupabaseClient } from "@supabase/supabase-js";

// Everything the site knows how to ask the backend.
//
// One rule: the site never touches a table. Every call goes through an Edge Function, which is where
// the rules live (who may see what, what a booking costs, when a slot is really free). That's why the
// anon key can be public — the database is behind RLS and each function checks its own caller.

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** No Supabase configured (a fresh clone, or a preview build) — the site says so rather than failing oddly. */
export const configured = Boolean(url && key);

let client: SupabaseClient | null = null;
function db(): SupabaseClient {
  if (!client) {
    if (!configured) throw new ApiError("This site isn't connected to its backend yet.", 0);
    client = createClient(url!, key!, { auth: { persistSession: true, autoRefreshToken: true } });
  }
  return client;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly issues?: unknown) {
    super(message);
  }
}

/**
 * One retry, for the blips that aren't anyone's fault.
 *
 * A gateway 5xx (we've seen "JWT issued at future" from a cold edge) is transient, and a parent who
 * has just described their child should not be told "that didn't work" because of it. Anything the
 * function itself decided — a 4xx, a validation error — is returned immediately: those are answers,
 * not failures, and repeating them would only slow the person down.
 */
async function invokeWithRetry(fn: string, body: Record<string, unknown>) {
  const first = await db().functions.invoke(fn, { body });
  const status = ((first.error as { context?: Response } | null)?.context?.status) ?? 0;
  const worthRetrying = first.error && (status === 0 || status >= 500);
  if (!worthRetrying) return first;
  await new Promise((r) => setTimeout(r, 600));
  return db().functions.invoke(fn, { body });
}

/**
 * Call an Edge Function. Errors come back as `{ error }` with a status, and we surface the message
 * the backend wrote: those are written for people, not for logs.
 */
export async function call<T>(fn: string, body: Record<string, unknown> = {}): Promise<T> {
  // No project configured: run the real matching and slot code against the sample tutors, so the site
  // can be clicked through before a backend exists. Every screen says it's a demo.
  if (!configured) {
    const { demoCall } = await import("./demo.ts");
    return demoCall<T>(fn, body);
  }
  const { data, error } = await invokeWithRetry(fn, body);
  if (error) {
    // supabase-js hides the body on non-2xx, so read it back for the real message.
    const res = (error as { context?: Response }).context;
    if (res && typeof res.json === "function") {
      const payload = await res.json().catch(() => null) as { error?: string; issues?: unknown } | null;
      throw new ApiError(payload?.error ?? error.message, res.status ?? 500, payload?.issues);
    }
    throw new ApiError(error.message, 500);
  }
  const payload = data as T & { error?: string };
  if (payload && typeof payload === "object" && "error" in payload && payload.error) throw new ApiError(String(payload.error), 400);
  return payload;
}

// ─── Shapes the pages use ────────────────────────────────────────────────────
// Only what the UI reads. The backend owns the full types; these are the promise it makes.

export interface PublicTutor {
  id: string;
  name: string;
  headline: string;
  bio: string;
  photo_url: string | null;
  first_lesson: string;
  modes: ("online" | "in_person")[];
  location: string | null;
  travel_radius_km: number | null;
  timezone: string;
  hourly_rate_aed: number;
  online_rate_aed: number;
  years_experience: number;
  qualifications: string[];
  subjects: { subject: string; age_min: number; age_max: number; curricula: string[]; notes: string }[];
  languages: { language: string; native: boolean }[];
  specialisms: string[];
  teaching_style: Record<string, { score: number; confidence: number }>;
  verified: { id: boolean; permit: boolean; credentials?: boolean; background_check: boolean; references: boolean };
  track_record: { trials: number; continuing: number; avg_rating: number | null; rating_count: number; quote?: string | null };
  /** Earned from what happened, lost when it stops. See _shared/badges.ts. */
  badges?: { id: string; label: string; why: string }[];
  accepting_students: boolean;
  booking_slug: string | null;
  late_cancellation_fee_pct: number;
  booking: {
    needs_approval: boolean;
    min_notice_hours: number;
    accept_window_hours: number;
    typical_reply_hours: number | null;
    reply_rate: number | null;
    accept_rate: number | null;
  };
}

export interface Slot {
  day: number;
  start: string;
  end: string;
  rate_aed: number;
  normal_rate_aed: number;
  price_note: string | null;
}

export type Venue = "family_home" | "tutor_home" | "online";

export interface BookingPageResult {
  tutor: PublicTutor & { offers: Record<Venue, boolean>; areas: string[] };
  slots?: Slot[];
  reason?: string;
}

export const api = {
  options: () => call<{
    curricula: { value: string; label: string }[];
    specialisms: { value: string; label: string }[];
    traits: { value: string; label: string }[];
    areas: string[];
    area_groups: { emirate: string; areas: string[] }[];
    days: { value: number; label: string }[];
    payment: { family: { title: string; items: string[] }; tutor: { title: string; items: string[] } };
    booking_policy: { min_notice_hours: number; accept_window_hours: number };
    coming_soon: { id: string; title: string; blurb: string; for_you: string; audience: string; asks: string[]; cta: string }[];
  }>("form-options"),

  tutors: () => call<{ tutors: PublicTutor[] }>("browse-tutors"),

  bookingPage: (ref: { slug?: string; match_id?: string }, family?: { venue: Venue; area?: string | null; lat?: number | null; lng?: number | null; lesson_minutes?: number }) =>
    call<BookingPageResult>("booking-page", { ...ref, ...(family ?? {}) }),

  createBooking: (body: Record<string, unknown>) =>
    call<{ booking: { id: string; day: number; start: string; starts_on: string; rate_aed: number }; payment_url: string | null; next_step: string; expires_at: string | null; explain: { family: string; payment: string } }>("create-booking", body),

  ask: (body: { slug?: string; match_id?: string; family_ref: string; question?: string }) =>
    call<{ questions: { id: string; asked_by: string; body: string; redacted: boolean; created_at: string }[]; left: number | null; notice: string; typical_reply_hours: number | null }>("ask-tutor", body),

  lesson: (body: Record<string, unknown>) => call<Record<string, unknown>>("lesson-followup", body),

  waitlist: (body: Record<string, unknown>) => call<{ features?: unknown[]; ok?: boolean; message?: string }>("waitlist", body),

  request: (body: Record<string, unknown>) => call<{ ok: true; token: string | null }>("submit-request", body),

  applyAsTutor: (body: Record<string, unknown>) => call<{ ok: true; token: string | null }>("submit-tutor", body),

  /** A family's own profile, by their private link: their children, saved so they never retype them. */
  familyProfile: (body: Record<string, unknown>) => call<Record<string, unknown>>("family-profile", body),

  /** "I lost my link." Always answers the same way, so it can't be used to find out who tutors with us. */
  tutorLink: (body: Record<string, unknown>) => call<{ ok: true; message: string }>("tutor-link", body),

  /** The sections a tutor finishes later, from their own private link. */
  tutorProfile: (body: Record<string, unknown>) => call<Record<string, unknown>>("tutor-profile", body),
};
